import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { FinalDeliveryOption } from '@/lib/subscription'

interface FinalDeliveryPanelProps {
  question: string
  groupLabel: string
  confirmLabel: string
  options: FinalDeliveryOption[]
  isSubmitting: boolean
  onConfirm: (finalSequence: number | null) => void
  onCancel: () => void
}

interface FinalDeliveryOptionButtonProps {
  option: FinalDeliveryOption
  isSelected: boolean
  sequence: number
  isSubmitting: boolean
  onSelect: (option: FinalDeliveryOption) => void
  onSequenceChange: (sequence: number) => void
}

const CANCEL_LABEL = 'Avbryt'
const SEQUENCE_LABEL = 'Leverans nummer'

const optionBaseClass = 'w-full rounded-none border px-4 py-3 text-left transition-colors cursor-pointer'
const selectedOptionClass = 'border-primary bg-primary/5'
const unselectedOptionClass = 'border-border hover:bg-muted/50'

function FinalDeliveryOptionButton({
  option,
  isSelected,
  sequence,
  isSubmitting,
  onSelect,
  onSequenceChange
}: FinalDeliveryOptionButtonProps): React.ReactElement {
  const optionClass = isSelected ? selectedOptionClass : unselectedOptionClass
  const showsNumber = option.takesNumber && isSelected

  const handleClick = (): void => {
    onSelect(option)
  }

  const handleSequenceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const next = Number(event.target.value)

    if (Number.isInteger(next) && next > 0) onSequenceChange(next)
  }

  return (
    <div className={cn(optionBaseClass, optionClass)}>
      <button
        type="button"
        role="radio"
        aria-checked={isSelected}
        onClick={handleClick}
        className="w-full text-left"
      >
        <p className="font-medium">{option.label}</p>
        <p className="mt-1 text-sm text-muted-foreground">{option.description}</p>
      </button>
      {showsNumber && (
        <label className="mt-3 flex items-center gap-3 text-sm">
          <span>{SEQUENCE_LABEL}</span>
          <Input
            type="number"
            min={1}
            value={sequence}
            disabled={isSubmitting}
            onChange={handleSequenceChange}
            className="w-24"
            aria-label={SEQUENCE_LABEL}
          />
        </label>
      )}
    </div>
  )
}

export function FinalDeliveryPanel({
  question,
  groupLabel,
  confirmLabel,
  options,
  isSubmitting,
  onConfirm,
  onCancel
}: FinalDeliveryPanelProps): React.ReactElement {
  const defaultOption = options.find((option) => option.isDefault) ?? null
  const [selectedOption, setSelectedOption] = useState<FinalDeliveryOption | null>(defaultOption)
  const [sequence, setSequence] = useState<number>(defaultOption?.finalSequence ?? 1)

  const canConfirm = selectedOption !== null && !isSubmitting

  const handleSelect = (option: FinalDeliveryOption): void => {
    setSelectedOption(option)

    if (option.takesNumber && option.finalSequence !== null) setSequence(option.finalSequence)
  }

  const handleConfirm = (): void => {
    if (!selectedOption) {
      return
    }

    onConfirm(selectedOption.takesNumber ? sequence : selectedOption.finalSequence)
  }

  return (
    <div className="space-y-3 border border-border/80 p-4">
      <p className="text-sm font-medium">{question}</p>

      <div
        role="radiogroup"
        aria-label={groupLabel}
        className="space-y-2"
      >
        {options.map((option) => (
          <FinalDeliveryOptionButton
            key={option.label}
            option={option}
            isSelected={option.label === selectedOption?.label}
            sequence={sequence}
            isSubmitting={isSubmitting}
            onSelect={handleSelect}
            onSequenceChange={setSequence}
          />
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          className="rounded-none"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          {CANCEL_LABEL}
        </Button>
        <Button
          variant="destructive"
          className="rounded-none"
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  )
}
